import React, { useMemo } from 'react';
import { RangeDatepicker, SingleDatepicker, DatepickerConfigs } from 'chakra-dayzed-datepicker';
import { i18n } from '@lingui/core';
// eslint-disable-next-line import/no-extraneous-dependencies
import moment from 'moment';
// eslint-disable-next-line import/no-extraneous-dependencies
import 'moment/locale/ru';
// eslint-disable-next-line import/no-extraneous-dependencies
import 'moment/locale/en-gb';

// Dil ayarları
const datePickerConfigs: Record<string, DatepickerConfigs> = {
  tr: {
    dateFormat: 'dd.MM.yyyy',
    monthNames: [
      'Ocak',
      'Şubat',
      'Mart',
      'Nisan',
      'Mayıs',
      'Haziran',
      'Temmuz',
      'Ağustos',
      'Eylül',
      'Ekim',
      'Kasım',
      'Aralık',
    ],
    dayNames: ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'],
    firstDayOfWeek: 1, // Pazartesi
  },
  en: {
    dateFormat: 'dd/MM/yyyy',
    monthNames: [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ],
    dayNames: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    firstDayOfWeek: 0, // Sunday
  },
  ru: {
    dateFormat: 'dd.MM.yyyy',
    monthNames: [
      'Январь',
      'Февраль',
      'Март',
      'Апрель',
      'Май',
      'Июнь',
      'Июль',
      'Август',
      'Сентябрь',
      'Октябрь',
      'Ноябрь',
      'Декабрь',
    ],
    dayNames: ['Воскресенье', 'Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота'],
    firstDayOfWeek: 1, // Понедельник
  },
};

interface LocalizedRangeDatepickerProps {
  selectedDates: Date[];
  onDateChange: (dates: Date[]) => void;
  minDate?: Date;
  maxDate?: Date;
  disabled?: boolean;
  usePortal?: boolean;
  id?: string;
  name?: string;
}

export const LocalizedRangeDatepicker: React.FC<LocalizedRangeDatepickerProps> = ({
  selectedDates,
  onDateChange,
  minDate,
  maxDate,
  disabled = false,
  usePortal = false,
  id,
  name,
}) => {
  // Get current language from Lingui i18n
  const currentLocale = i18n.locale || 'tr';

  // Set moment locale to match
  useMemo(() => {
    if (currentLocale === 'en') {
      moment.locale('en-gb');
    } else {
      moment.locale(currentLocale);
    }
  }, [currentLocale]);

  const config = datePickerConfigs[currentLocale] || datePickerConfigs.tr;

  return (
    <RangeDatepicker
      configs={config}
      selectedDates={selectedDates}
      onDateChange={onDateChange}
      minDate={minDate}
      maxDate={maxDate}
      disabled={disabled}
      usePortal={usePortal}
      id={id}
      name={name}
    />
  );
};

interface LocalizedSingleDatepickerProps {
  date?: Date;
  onDateChange: (date: Date) => void;
  minDate?: Date;
  maxDate?: Date;
  disabled?: boolean;
  usePortal?: boolean;
  id?: string;
  name?: string;
}

export const LocalizedSingleDatepicker: React.FC<LocalizedSingleDatepickerProps> = ({
  date,
  onDateChange,
  minDate,
  maxDate,
  disabled = false,
  usePortal = false,
  id,
  name,
}) => {
  // Get current language from Lingui i18n
  const currentLocale = i18n.locale || 'tr';

  // Set moment locale to match
  useMemo(() => {
    if (currentLocale === 'en') {
      moment.locale('en-gb');
    } else {
      moment.locale(currentLocale);
    }
  }, [currentLocale]);

  const config = datePickerConfigs[currentLocale] || datePickerConfigs.tr;

  return (
    <SingleDatepicker
      configs={config}
      date={date}
      onDateChange={onDateChange}
      minDate={minDate}
      maxDate={maxDate}
      disabled={disabled}
      usePortal={usePortal}
      id={id}
      name={name}
    />
  );
};
